import { WORLD_H, WORLD_W } from '../constants.js';
import { Rng } from '../rng.js';
import type { Prop, ThemeId, WaterRegion } from '../types.js';
import { THEMES } from '../data/themes.js';
import { createTerrain, isSolid, surfaceY } from './mask.js';
import { placeProps } from './props.js';

export interface GeneratedMap {
  seed: number;
  theme: ThemeId;
  terrain: Uint8Array;
  water: WaterRegion[];
  props: Prop[];
  /** One flat spawn slot list per team, in team order. */
  teamSpawns: { x: number; y: number }[][];
  decorSeed: number;
}

// ---------------------------------------------------------------------------
// Stateless value noise (deterministic, seed-mixed)
// ---------------------------------------------------------------------------

function hash1(n: number): number {
  let t = (n + 0x9e3779b9) | 0;
  t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
  t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
  return ((t ^ (t >>> 15)) >>> 0) / 4294967296;
}

function vnoise(x: number, salt: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const a = hash1(i * 374761393 + salt);
  const b = hash1((i + 1) * 374761393 + salt);
  const u = f * f * (3 - 2 * f);
  return a + (b - a) * u;
}

// ---------------------------------------------------------------------------
// Map generation
// ---------------------------------------------------------------------------

export function generateMap(seed: number, themeId: ThemeId, spawnPlan: number[]): GeneratedMap {
  const theme = THEMES[themeId];
  const rng = new Rng(seed);
  const salt = seed | 0;
  const terrain = createTerrain(0);

  // 1) Height profile, sampled every 4px.
  const base = WORLD_H * 0.56;
  const relief = theme.relief;
  const rough = theme.roughness;
  const profX = 4;
  const n = Math.ceil(WORLD_W / profX) + 1;
  const profile = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = i * profX;
    const h =
      base +
      (vnoise(x / 1150, salt) * 2 - 1) * 175 * relief +
      (vnoise(x / 430, salt + 7717) * 2 - 1) * 72 * rough +
      (vnoise(x / 165, salt + 9929) * 2 - 1) * 26 * rough;
    profile[i] = Math.min(WORLD_H - 150, Math.max(240, h));
  }

  const profileAt = (x: number): number => {
    const fx = x / profX;
    const i = Math.min(n - 2, Math.max(0, Math.floor(fx)));
    const f = fx - i;
    return profile[i] * (1 - f) + profile[i + 1] * f;
  };

  // 1.5) Terraced plateaus (canyon mesas, highland steps): snap the profile
  // to steps, then one [1,2,1] pass so cliffs read as sheer faces instead of
  // single-column stair noise. Plateau tops stay flat — great roof-hop fights.
  if (theme.terraces && theme.terraces > 0) {
    const step = theme.terraces;
    const snapped = new Float32Array(n);
    for (let i = 0; i < n; i++) snapped[i] = Math.round(profile[i] / step) * step;
    profile[0] = snapped[0];
    profile[n - 1] = snapped[n - 1];
    for (let i = 1; i < n - 1; i++) {
      profile[i] = (snapped[i - 1] + 2 * snapped[i] + snapped[i + 1]) / 4;
    }
    for (let i = 0; i < n; i++) {
      profile[i] = Math.min(WORLD_H - 150, Math.max(240, profile[i]));
    }
  }

  // 2) Fill columns below the profile.
  for (let x = 0; x < WORLD_W; x++) {
    const top = Math.floor(profileAt(x));
    for (let y = top; y < WORLD_H; y++) terrain[y * WORLD_W + x] = 1;
  }

  // 3) Caves (only deep under the surface so spawn pads stay intact).
  if (rng.next() < theme.caveChance) {
    const caveCount = rng.int(1, 3);
    for (let c = 0; c < caveCount; c++) {
      const cx = rng.range(WORLD_W * 0.15, WORLD_W * 0.85);
      const ry = rng.range(46, 100);
      const cy = profileAt(cx) + rng.range(130, 330);
      const rx = rng.range(90, 190);
      if (cy + ry > WORLD_H - 30) continue;
      const x0 = Math.max(1, Math.floor(cx - rx));
      const x1 = Math.min(WORLD_W - 2, Math.ceil(cx + rx));
      const y0 = Math.max(1, Math.floor(cy - ry));
      const y1 = Math.min(WORLD_H - 2, Math.ceil(cy + ry));
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const dx = (x - cx) / rx;
          const dy = (y - cy) / ry;
          // Blobbier than a pure ellipse.
          const wob = 1 + (vnoise(x / 55 + y / 70, salt + c * 31) - 0.5) * 0.55;
          if (dx * dx + dy * dy <= wob && y > profileAt(x) + 55) {
            terrain[y * WORLD_W + x] = 0;
          }
        }
      }
    }
  }

  // 4) Water pools. Water-heavy themes (islands) cut several wide channels so
  // the land breaks into separate landing zones instead of one shore pool —
  // and the harbor cuts one wide working bay for its ships to sit in.
  const water: WaterRegion[] = [];
  const carvePool = (left: number, right: number, depth: number): void => {
    const edgeL = profileAt(left);
    const edgeR = profileAt(right);
    const waterTop = Math.max(edgeL, edgeR) + 6;
    for (let x = Math.floor(left); x <= Math.ceil(right); x++) {
      const s = Math.floor(profileAt(x));
      const bottom = Math.min(WORLD_H - 1, Math.floor(s + depth));
      for (let y = s; y <= bottom; y++) terrain[y * WORLD_W + x] = 0;
    }
    water.push({
      x: Math.floor(left),
      y: Math.floor(waterTop),
      w: Math.ceil(right - left),
      h: WORLD_H - Math.floor(waterTop),
    });
  };
  if (themeId === 'harbor') {
    carvePool(WORLD_W * 0.1, WORLD_W * 0.9, rng.range(110, 150));
  } else if (rng.next() < theme.waterChance) {
    const archipelago = theme.waterChance >= 0.7;
    const pools = archipelago ? rng.int(3, 5) : rng.int(1, 2);
    for (let p = 0; p < pools; p++) {
      const w = archipelago ? rng.range(240, 480) : rng.range(210, 380);
      const cx = rng.range(WORLD_W * 0.22, WORLD_W * 0.78);
      const left = Math.max(40, cx - w / 2);
      const right = Math.min(WORLD_W - 40, cx + w / 2);
      // Never a pool deeper than it is wide — keeps banks believable.
      carvePool(left, right, rng.range(70, 120));
    }
  }

  // 4.5) Map structures — the thing you fight AROUND, not just on. Bases
  // (buildings, ships, buttes, towers) go in BEFORE spawn-picking so their
  // flat tops become spawn pads; thin details (masts, water tanks, spires)
  // come AFTER, once the pads are known, so pad-flattening can't sever them.
  const structs = stampStructures(terrain, rng, themeId, profileAt, water);

  // 5) Spawn slots: flat pads, one list per team.
  // A column inside a water rect only counts as "in water" when there is no
  // solid surface above the waterline — ship decks and piers stand IN the bay
  // and must stay spawnable.
  const inAnyWater = (x: number): boolean =>
    water.some((wl) => {
      if (x < wl.x - 8 || x > wl.x + wl.w + 8) return false;
      const sy = surfaceY(terrain, x);
      return sy < 0 || sy >= wl.y - 4;
    });

  const teamSpawns: { x: number; y: number }[][] = [];
  const taken: { x: number; y: number }[] = [];

  // Two phase-2 igloo carves can eat a pad that was perfectly good at pick
  // time (the igloo block is the one detail pass without a clearOfPads
  // guard), so spawn picking steers around them itself:
  //  - interior: a fill straddling the room edge dips below the ceiling
  //    curve and loses its outer columns — full containment is fine (the
  //    crown slab survives), full clearance is fine, straddling never is;
  //  - doorway: the door punches [g-26, floorY) on ONE side — rolled in
  //  phase 2, so guard both sides — and a ground-level pad whose fill
  //    spans those columns loses its top. Fills whose top sits above g-26
  //    (or below the base) keep their surface and stay allowed.
  const padBlocked = (x: number, y: number): boolean =>
    structs.igloos.some((ig) => {
      const ir = ig.r - 16;
      const lo = ig.cx - ir;
      const hi = ig.cx + ir;
      const x0 = x - 35;
      const x1 = x + 35;
      const overlap = x0 < hi && x1 > lo;
      const inside = x0 >= lo && x1 <= hi;
      if (overlap && !inside) return true;
      if (y < ig.g - 26 || y >= ig.g) return false;
      const dl = ig.r - 20;
      const dr = ig.r + 2;
      return (
        (x0 < ig.cx - dl && x1 > ig.cx - dr) ||
        (x0 < ig.cx + dr && x1 > ig.cx + dl)
      );
    });

  for (let team = 0; team < spawnPlan.length; team++) {
    const count = Math.max(1, spawnPlan[team]);
    const range = spawnRange(team, spawnPlan.length);
    const placed = pickSpawnSlots(terrain, rng, range, count, taken, inAnyWater, profileAt, padBlocked);
    for (const s of placed) taken.push(s);
    teamSpawns.push(placed);
  }

  // 5.5) Thin details now that pads are frozen (they keep clear of them).
  stampStructureDetails(terrain, rng, themeId, structs, taken, profileAt);

  // 6) Props (avoiding every spawn and pool).
  const props = placeProps(terrain, theme, rng, taken, water);

  return {
    seed,
    theme: themeId,
    terrain,
    water,
    props,
    teamSpawns,
    decorSeed: rng.int(1, 1 << 30),
  };
}

function spawnRange(
  team: number,
  teamCount: number,
): { x0: number; x1: number } {
  if (teamCount <= 2) {
    // Bases on opposite sides.
    return team === 0
      ? { x0: WORLD_W * 0.06, x1: WORLD_W * 0.4 }
      : { x0: WORLD_W * 0.6, x1: WORLD_W * 0.94 };
  }
  // FFA: slice the width per team.
  const slice = WORLD_W / teamCount;
  const cx = slice * (team + 0.5);
  const half = Math.min(slice * 0.38, WORLD_W * 0.16);
  return { x0: cx - half, x1: cx + half };
}

function pickSpawnSlots(
  terrain: Uint8Array,
  rng: Rng,
  range: { x0: number; x1: number },
  count: number,
  taken: { x: number; y: number }[],
  inWater: (x: number) => boolean,
  profileAt: (x: number) => number,
  blocked: (x: number, y: number) => boolean,
): { x: number; y: number }[] {
  const span = range.x1 - range.x0;
  let spacing = Math.max(120, Math.min(250, span / (count + 0.5)));

  while (spacing >= 60) {
    const out: { x: number; y: number }[] = [];
    let ok = true;
    for (let k = 0; k < count && ok; k++) {
      const targetX = range.x0 + ((k + 0.5) / count) * span;
      const slot = findPadNear(terrain, targetX, range, spacing, taken, out, inWater, rng, blocked);
      if (!slot) {
        ok = false;
        break;
      }
      out.push(slot);
    }
    // Validate BEFORE mutating: flatten only when the whole set fits.
    if (ok && spacingOK(out, taken, 90)) {
      for (const slot of out) flattenPad(terrain, slot.x, slot.y);
      return out;
    }
    spacing -= 25;
  }

  // Last resort: force-place from evenly spaced bases (flat, spacing be
  // damned). Walk outward through ordered passes — dry + gentle + spaced
  // first, then relax spacing, then gentleness, and only as a last hope the
  // raw base column — because a base inside a pool drowns on turn one, a
  // pad built against a wall stands in its shadow, and stacked slots eat
  // each other's fill.
  const out: { x: number; y: number }[] = [];
  const offs: number[] = [0];
  for (let d = 16; d <= 480; d += 16) offs.push(d, -d);
  const column = (cx: number): number => {
    const y = surfaceY(terrain, cx);
    return y < 0 || y > WORLD_H * 0.85 ? Math.floor(profileAt(cx)) : y;
  };
  const passes = [
    { dry: true, gentle: true, far: 100 },
    { dry: true, gentle: true, far: 90 },
    { dry: true, gentle: true, far: 0 },
    { dry: true, gentle: false, far: 90 },
    { dry: true, gentle: false, far: 0 },
    { dry: false, gentle: false, far: 0 },
  ];
  for (let k = 0; k < count; k++) {
    const base = Math.round(range.x0 + ((k + 0.5) / count) * span);
    let placed: { x: number; y: number } | null = null;
    for (const pass of passes) {
      for (const off of offs) {
        const cx = base + off;
        if (cx < 50 || cx > WORLD_W - 50) continue;
        if (cx < range.x0 - 15 || cx > range.x1 + 15) continue;
        if (pass.dry && inWater(cx)) continue;
        const cy = column(cx);
        if (blocked(cx, cy)) continue;
        if (pass.gentle && !gentlePad(terrain, cx, cy)) continue;
        if (
          pass.far > 0 &&
          !taken.concat(out).every((s) => Math.abs(s.x - cx) >= pass.far)
        ) {
          continue;
        }
        placed = { x: cx, y: cy };
        break;
      }
      if (placed) break;
    }
    if (!placed) placed = { x: base, y: column(base) };
    flattenPad(terrain, placed.x, placed.y);
    out.push(placed);
  }
  return out;
}

function spacingOK(
  slots: { x: number; y: number }[],
  taken: { x: number; y: number }[],
  min: number,
): boolean {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (Math.abs(slots[i].x - slots[j].x) < min) return false;
    }
    for (const t of taken) {
      if (Math.abs(slots[i].x - t.x) < 90) return false;
    }
  }
  return true;
}

/**
 * Would a pad at (x, y) keep gentle, wall-free standing room? Probes at ±30
 * guard the spawn's own footprint; ±36 spans the whole flattened pad (±35)
 * plus its ±3 jitter. A wall, overhang or cliff that reads more than 30px
 * off the pad level disqualifies the spot — otherwise a butte flank or a
 * building wall can sit inside the spawn's standing room.
 */
function gentlePad(terrain: Uint8Array, x: number, y: number): boolean {
  for (const d of [0, -30, 30, -36, 36]) {
    const sy = surfaceY(terrain, x + d);
    if (sy < 0 || Math.abs(sy - y) > 30) return false;
  }
  return true;
}

function findPadNear(
  terrain: Uint8Array,
  targetX: number,
  range: { x0: number; x1: number },
  spacing: number,
  taken: { x: number; y: number }[],
  already: { x: number; y: number }[],
  inWater: (x: number) => boolean,
  rng: Rng,
  blocked: (x: number, y: number) => boolean,
): { x: number; y: number } | null {
  const offsets = [0];
  // Wide reach: a dry, gentle spot can sit just past the old ±260 cap when a
  // pool or a keepsake building crowds the middle of the team's slice — better
  // to find it here than to drop into the last-resort wanderer.
  for (let d = 12; d <= 400; d += 12) {
    offsets.push(d, -d);
  }
  for (const off of offsets) {
    const x = Math.round(targetX + off);
    if (x < 50 || x > WORLD_W - 50) continue;
    // Slots stay on their own team's side of the map.
    if (x < range.x0 - 15 || x > range.x1 + 15) continue;
    if (inWater(x)) continue;
    const all = taken.concat(already);
    if (all.some((s) => Math.abs(s.x - x) < spacing)) continue;
    const y = padSurface(terrain, x);
    if (y < 0) continue;
    // Jitter FIRST: the gentle check has to cover where the pad will really
    // land, not the pre-jitter column. (Probing ±26 before a ±3 jitter let a
    // butte overhang sit 28px from a spawn — inside the standing room the
    // old check never looked at.)
    const jx = x + Math.round(rng.range(-3, 3));
    if (blocked(jx, y)) continue;
    if (!gentlePad(terrain, jx, y)) continue;
    return { x: jx, y };
  }
  return null;
}

function padSurface(terrain: Uint8Array, x: number): number {
  // Only accept a surface in the upper 75% of the world (no deep pits).
  const y = surfaceY(terrain, x);
  if (y < 0 || y > WORLD_H * 0.8) return -1;
  return y;
}

/** Carve a 70px-wide flat pad at y: cuts highs, fills lows, bridges slopes. */
export function flattenPad(terrain: Uint8Array, x: number, y: number): void {
  for (let dx = -35; dx <= 35; dx++) {
    const px = x + dx;
    if (px < 0 || px >= WORLD_W) continue;
    for (let py = Math.max(0, y - 60); py < y; py++) {
      terrain[py * WORLD_W + px] = 0;
    }
    const bottom = Math.min(WORLD_H - 1, y + 50);
    for (let py = y; py <= bottom; py++) {
      terrain[py * WORLD_W + px] = 1;
    }
  }
}

// ---------------------------------------------------------------------------
// Map structures — every theme gets shapes worth fighting around
// ---------------------------------------------------------------------------

const WALL = 7; // facade thickness
const FLOOR_H = 54; // floor-to-floor (44px of headroom below a 10px slab)

/** Geometry handed from the base pass to the details pass. */
interface Structs {
  /** City building roofs (x0..x0+w, top row) — tanks go on the wide ones. */
  roofs: { x: number; w: number; roof: number }[];
  /** Harbor ships — masts/cabins/crates go on the decks. */
  ships: { x0: number; x1: number; deckY: number }[];
  /**
   * Igloos — stamped SOLID in phase 1 (so a crown pad's [y, y+50] fill can
   * never seal the room), hollowed out here in phase 2, after flattening.
   */
  igloos: { cx: number; g: number; r: number }[];
}

function fillRect(
  terrain: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  const xa = Math.max(0, Math.floor(x0));
  const xb = Math.min(WORLD_W - 1, Math.ceil(x1));
  const ya = Math.max(0, Math.floor(y0));
  const yb = Math.min(WORLD_H - 1, Math.ceil(y1));
  for (let x = xa; x <= xb; x++) {
    for (let y = ya; y <= yb; y++) terrain[y * WORLD_W + x] = 1;
  }
}

/**
 * Extend a structure's assumed base down to the LOCAL surface so its edges
 * never hover over a downhill slope (and never dig in on an uphill one —
 * the ground is already solid there, so the fill is a no-op).
 */
function seat(
  terrain: Uint8Array,
  x0: number,
  x1: number,
  fromY: number,
  profileAt: (x: number) => number,
  depth = 14,
): void {
  const xa = Math.max(0, Math.floor(x0));
  const xb = Math.min(WORLD_W - 1, Math.ceil(x1));
  for (let x = xa; x <= xb; x++) {
    let bottom = Math.floor(profileAt(x)) + depth;
    // A pool carved below the profile means the "ground" is farther down —
    // keep filling to the first solid so the base reads as a foundation,
    // never as a slab hovering over water.
    while (bottom < WORLD_H - 2 && !isSolid(terrain, x, bottom)) bottom++;
    if (bottom > fromY) fillRect(terrain, x, fromY, x, bottom);
  }
}

/**
 * One hollow block: solid facade, floors rising from the ground, window
 * slits through both walls (shoot a beehive straight into the top floor),
 * and a door at ground level. The roof stays flat and solid — prime spawn
 * real estate, so the spawn fitter finds it on its own. Each column is
 * seated down to the LOCAL ground, so a block on a slope never hovers.
 * Returns the roof's top row.
 */
function stampBlock(
  terrain: Uint8Array,
  rng: Rng,
  x: number,
  w: number,
  groundY: number,
  h: number,
  profileAt: (x: number) => number,
): number {
  const roof = Math.max(250, groundY - h);
  const x1 = Math.min(WORLD_W - 1, x + w - 1);
  for (let bx = x; bx <= x1; bx++) {
    let bottom = Math.max(groundY, Math.floor(profileAt(bx))) - 1;
    // Sink through anything carved below the profile (a canal under the
    // street) so the facade stands on the bed, not on the water.
    while (bottom < WORLD_H - 2 && !isSolid(terrain, bx, bottom + 1)) bottom++;
    fillRect(terrain, bx, roof, bx, bottom);
  }

  const innerX0 = x + WALL;
  const innerX1 = x1 - WALL;
  if (innerX1 <= innerX0) return roof;

  // Hollow the interior (roof slab stays 8px). The floor stops at the LOCAL
  // ground: never dig below the street on an uphill side; on a downhill side
  // the extra column depth stays solid as a foundation.
  for (let bx = innerX0; bx <= innerX1; bx++) {
    const floorY = Math.min(groundY, Math.floor(profileAt(bx)));
    for (let y = roof + 8; y < floorY; y++) terrain[y * WORLD_W + bx] = 0;
  }
  // Floor slabs rising from the ground: 10px thick every FLOOR_H.
  for (let k = 1; ; k++) {
    const slabTop = groundY - k * FLOOR_H;
    if (slabTop < roof + 14) break;
    for (let bx = innerX0; bx <= innerX1; bx++) {
      for (let t = 0; t < 10; t++) terrain[(slabTop + t) * WORLD_W + bx] = 1;
    }
    // One window per floor, punched through both facades at torso height.
    const wy = slabTop + 14;
    if (wy < groundY - 8 && wy > roof + 10 && rng.next() < 0.85) {
      for (let t = 0; t < 12; t++) {
        const y = wy + t;
        for (let b = 0; b < WALL; b++) {
          terrain[y * WORLD_W + (x + b)] = 0;
          terrain[y * WORLD_W + (x1 - b)] = 0;
        }
      }
    }
  }
  // Ground-level door through one facade — walk in off the pavement. The
  // opening spans from the interior floor down to the LOCAL street, so a
  // door on a slope never opens into solid earth.
  const doorX = rng.next() < 0.5 ? x : x1;
  const street = Math.floor(profileAt(doorX));
  const dTop = Math.min(groundY, street) - 34;
  for (let y = dTop; y < street; y++) {
    if (y < 0 || y >= WORLD_H) continue;
    for (let b = 0; b < WALL; b++) {
      const px = doorX === x ? x + b : x1 - b;
      if (px >= 0 && px < WORLD_W) terrain[y * WORLD_W + px] = 0;
    }
  }
  return roof;
}

/**
 * Phase 1 — chunky bases stamped BEFORE spawn-picking so their flat tops
 * become spawn pads (roofs, decks, mesas, tower crowns). Everything here is
 * solid down to the ground: pad-flattening only ever notches an edge, never
 * severs a leg. Thin fare (masts, tanks, spires) waits for phase 2.
 */
function stampStructures(
  terrain: Uint8Array,
  rng: Rng,
  themeId: ThemeId,
  profileAt: (x: number) => number,
  water: WaterRegion[],
): Structs {
  const structs: Structs = { roofs: [], ships: [], igloos: [] };

  const at = (x: number): number => Math.floor(profileAt(x));

  /**
   * Nudge a DRY-land structure off any pool. Ships/piers/rafts want water;
   * towers, igloos and keeps with rooms must not straddle a shoreline
   * (their floors would flood). Returns the original x when nothing fits.
   */
  const dryGround = (x: number): number => {
    const inPool = (cx: number): boolean =>
      water.some((w) => cx >= w.x - 96 && cx <= w.x + w.w + 96);
    if (!inPool(x)) return x;
    for (let d = 24; d <= 420; d += 24) {
      if (!inPool(x + d)) return x + d;
      if (!inPool(x - d)) return x - d;
    }
    return x; // a pool the size of the map — seat-to-bed keeps it standing
  };

  /**
   * Abstract-map monoliths: solid columns on dry ground, domed or flat tops.
   * Planted phase-1 like the watchtowers — a spawn pad may notch a crown,
   * never sever a leg.
   */
  const stampPillars = (
    count: number,
    opts: { minH: number; maxH: number; minW: number; maxW: number; rect?: boolean },
  ): void => {
    for (let i = 0; i < count; i++) {
      const raw = ((i + 0.5) / count) * WORLD_W + rng.range(-70, 70);
      const cx = Math.round(dryGround(Math.min(WORLD_W - 60, Math.max(60, raw))));
      const g = at(cx);
      if (g < 0) continue;
      const w = rng.int(opts.minW, opts.maxW);
      const h = rng.int(opts.minH, opts.maxH);
      const top = Math.max(260, g - h); // never scrape the sky ceiling
      const x0 = cx - Math.floor(w / 2);
      const x1 = cx + Math.floor(w / 2);
      fillRect(terrain, x0, top, x1, g);
      seat(terrain, x0, x1, g, profileAt);
      if (!opts.rect) {
        // Domed cap: a half-disc above the shaft.
        const r = Math.floor(w / 2);
        for (let dx = -r; dx <= r; dx++) {
          const dy = Math.round(Math.sqrt(Math.max(0, r * r - dx * dx)));
          if (dy > 0) fillRect(terrain, cx + dx, top - dy, cx + dx, top);
        }
      }
    }
  };

  /** Ziggurat steps: each tier narrower than the one below, rising from the ground. */
  const stampSteps = (rawX: number, baseW: number, tiers: number, tierH: number): void => {
    const cx = Math.round(dryGround(rawX));
    const g = at(cx);
    if (g < 0) return;
    let w = baseW;
    let top = g;
    for (let t = 0; t < tiers; t++) {
      const h = tierH + rng.int(-6, 8);
      const y0 = Math.max(260, top - h);
      const x0 = cx - Math.floor(w / 2);
      const x1 = cx + Math.floor(w / 2);
      fillRect(terrain, x0, y0, x1, top);
      if (t === 0) seat(terrain, x0, x1, g, profileAt, 10);
      top = y0;
      w = Math.max(44, w - rng.int(28, 46));
    }
  };

  switch (themeId) {
    case 'city': {
      // Streets of hollow blocks with roofs for spawn/sniper pads.
      let x = 30;
      while (x < WORLD_W - 70) {
        const w = rng.int(90, 190);
        const street = at(x + w / 2);
        const roof = stampBlock(terrain, rng, x, w, street, rng.int(140, 460), profileAt);
        structs.roofs.push({ x, w, roof });
        // Street gap ≥75px — spawn pads are 70px wide and need room.
        x += w + rng.int(75, 150);
      }
      break;
    }

    case 'skyline':
    case 'neon': {
      // Downtown: the city's hollow-block street, but taller — extra floors
      // for rooftop fights, same ≥75px street gaps so spawn pads fit between.
      let x = 30;
      while (x < WORLD_W - 70) {
        const w = rng.int(90, 190);
        const street = at(x + w / 2);
        const roof = stampBlock(terrain, rng, x, w, street, rng.int(280, 660), profileAt);
        structs.roofs.push({ x, w, roof });
        x += w + rng.int(75, 150);
      }
      break;
    }

    case 'harbor': {
      // One bay (carved in the water pass), two ships facing each other,
      // piers reaching in from the shores, low warehouses behind them.
      const bay = water[0];
      if (bay) {
        for (const [cx, side] of [
          [WORLD_W * 0.3, 1],
          [WORLD_W * 0.7, -1],
        ] as const) {
          const len = rng.int(360, 430);
          const deckY = bay.y - 14;
          const x0 = Math.floor(cx - len / 2);
          const x1 = Math.floor(cx + len / 2);
          const depth = rng.int(85, 110);
          for (let x = x0; x <= x1; x++) {
            // Rounded bow and stern: ends pull in by up to 30px.
            const t = Math.min(x - x0, x1 - x) / len; // 0 at ends → .5 mid
            const endInset = Math.round((1 - Math.min(1, t / 0.15)) * 30);
            for (let y = deckY; y <= deckY + depth; y++) {
              const k = (y - deckY) / depth;
              const inset = endInset + Math.round(k * k * len * 0.1);
              if (x >= x0 + inset && x <= x1 - inset) {
                if (x >= 0 && x < WORLD_W && y < WORLD_H) {
                  terrain[y * WORLD_W + x] = 1;
                }
              }
            }
          }
          structs.ships.push({ x0, x1, deckY });

          // Pier from this side's shore out into the bay.
          const fromX = side === 1 ? Math.floor(bay.x) - 30 : Math.floor(bay.x + bay.w) + 30;
          const dir = side;
          const plen = rng.int(210, 300);
          const pierY = bay.y - 10;
          for (let i = 0; i < plen; i++) {
            const px = fromX + dir * i;
            if (px < 2 || px >= WORLD_W - 2) break;
            fillRect(terrain, px, pierY, px, pierY + 13);
            // Stilt every ~55px (3px thick), down to the first solid.
            if (i % 55 === 12) {
              for (const lx of [px - 1, px, px + 1]) {
                if (lx < 0 || lx >= WORLD_W) continue;
                let y = pierY + 14;
                while (y < WORLD_H - 2 && !isSolid(terrain, lx, y)) {
                  terrain[y * WORLD_W + lx] = 1;
                  y++;
                }
              }
            }
          }
        }
      }
      // Low warehouses on both shores.
      for (const side of [0, 1]) {
        const w = rng.int(110, 160);
        const x = side === 0
          ? rng.int(30, Math.max(31, Math.floor(WORLD_W * 0.1) - w - 10))
          : rng.int(Math.ceil(WORLD_W * 0.9) + 10, WORLD_W - 30 - w);
        const gy = at(x + w / 2);
        const roof = stampBlock(terrain, rng, x, w, gy, rng.int(60, 110), profileAt);
        structs.roofs.push({ x, w, roof });
      }
      break;
    }

    case 'meadow': {
      // Wooden watchtowers near each base: tapered solid columns (a pad
      // beside one only notches the flare) with a wide lookout platform.
      for (const raw of [WORLD_W * 0.22, WORLD_W * 0.78]) {
        const cx = dryGround(raw);
        const g = at(cx);
        const top = Math.max(250, g - Math.round(rng.range(150, 210)));
        const baseW = 132;
        const topW = 112; // matches the lookout platform, so the ledge sits ON the column
        for (let y = top; y < g; y++) {
          const t = (y - top) / Math.max(1, g - top);
          const half = Math.round((topW + (baseW - topW) * t) / 2);
          fillRect(terrain, cx - half, y, cx + half, y);
        }
        fillRect(terrain, cx - 56, top - 2, cx + 56, top + 9);
        seat(terrain, cx - baseW / 2, cx + baseW / 2, g, profileAt);
      }
      break;
    }

    case 'forest': {
      // Giant stump platforms near both bases…
      for (const raw of [WORLD_W * 0.2, WORLD_W * 0.8]) {
        const cx = dryGround(raw);
        const g = at(cx);
        const top = Math.max(250, g - rng.int(110, 190));
        const w = rng.int(92, 128);
        fillRect(terrain, cx - w / 2, top, cx + w / 2, g + 10);
        // Root flare into the hillside.
        for (let y = g - 26; y < g + 10; y++) {
          const ext = Math.round(((y - (g - 26)) / 36) * 14);
          fillRect(terrain, cx - w / 2 - ext, y, cx - w / 2 - 1, y);
          fillRect(terrain, cx + w / 2 + 1, y, cx + w / 2 + ext, y);
        }
        seat(terrain, cx - w / 2 - 14, cx + w / 2 + 14, g + 10, profileAt);
      }
      // …and a log bridge over a dip in the middle of the map.
      const x0 = Math.floor(WORLD_W * rng.range(0.34, 0.44));
      const x1 = x0 + rng.int(220, 320);
      const deckY = Math.max(
        250,
        Math.floor(Math.min(at(x0), at(x1)) - rng.range(70, 150)),
      );
      fillRect(terrain, x0, deckY, x1, deckY + 13);
      for (const sx of [x0, x1 - 14]) {
        for (let x = sx; x < sx + 14; x++) {
          fillRect(terrain, x, deckY + 14, x, at(x) - 1);
          seat(terrain, x, x, at(x), profileAt, 0); // pier legs to the bed
        }
      }
      break;
    }

    case 'desert': {
      // Stepped buttes — flat mesas to spawn on, sheer tiered walls between.
      for (const raw of [WORLD_W * 0.18, WORLD_W * 0.5, WORLD_W * 0.82]) {
        const cx = dryGround(raw);
        const g = at(cx);
        const top = Math.max(250, g - rng.int(190, 340));
        let half = Math.round(rng.int(130, 180) / 2);
        const bandH = Math.round((g - top) / 3);
        let y = top;
        for (let level = 0; level < 3; level++) {
          const y1 = level === 2 ? g : y + bandH;
          fillRect(terrain, cx - half, y, cx + half, y1 - 1);
          if (level === 2) seat(terrain, cx - half, cx + half, g, profileAt);
          y = y1;
          half = Math.max(34, half - 14);
        }
      }
      break;
    }

    case 'snow': {
      // Igloos: dome stamped SOLID here; the room is hollowed in phase 2 so
      // a crown spawn pad's fill can't seal it. Roof stays ~13px at the crown.
      for (const raw of [WORLD_W * 0.24, WORLD_W * 0.52, WORLD_W * 0.78]) {
        const cx = dryGround(raw);
        const g = at(cx);
        const r = rng.int(58, 78);
        for (let dx = -r; dx <= r; dx++) {
          const x = Math.round(cx + dx);
          if (x < 1 || x >= WORLD_W - 1) continue;
          const domeY = g - Math.round(Math.sqrt(r * r - dx * dx) * 0.8);
          fillRect(terrain, x, domeY, x, g + 12);
        }
        seat(terrain, cx - r, cx + r, g + 12, profileAt);
        structs.igloos.push({ cx, g, r });
      }
      break;
    }

    case 'canyon': {
      // A natural rock arch: solid abutments at both ends, a flat deck you
      // can spawn and fight on, open sky (and a drop) beneath it.
      const cx = Math.floor(WORLD_W * rng.range(0.4, 0.6));
      const half = rng.int(95, 140);
      const x0 = cx - half;
      const x1 = cx + half;
      const g0 = at(x0);
      const g1 = at(x1);
      let midMin = Infinity;
      for (let x = x0; x <= x1; x++) midMin = Math.min(midMin, profileAt(x));
      const deckY = Math.max(
        250,
        Math.floor(Math.min(midMin - 60, (g0 + g1) / 2 - 40)),
      );
      fillRect(terrain, x0, deckY, x1, deckY + 17);
      for (let x = x0; x <= x0 + 45; x++) {
        fillRect(terrain, x, deckY, x, at(x) - 1);
        seat(terrain, x, x, at(x), profileAt, 0); // down to the riverbed
      }
      for (let x = x1 - 45; x <= x1; x++) {
        fillRect(terrain, x, deckY, x, at(x) - 1);
        seat(terrain, x, x, at(x), profileAt, 0);
      }
      break;
    }

    case 'highlands': {
      // Paired stone keeps — hollow towers with floors and arrow-slit
      // windows, built by the same block builder as the city.
      for (const raw of [WORLD_W * rng.range(0.2, 0.3), WORLD_W * rng.range(0.7, 0.8)]) {
        const cx = dryGround(raw);
        const w = rng.int(104, 140);
        stampBlock(terrain, rng, Math.floor(cx - w / 2), w, at(cx), rng.int(190, 300), profileAt);
      }
      break;
    }

    case 'islands':
    case 'lagoon': {
      // Rafts and stilt huts over the channels — deck above the waterline,
      // so the spawn fitter treats them as dry land.
      for (let i = 0; i < water.length; i++) {
        const wl = water[i];
        if (wl.w < 240) continue; // room for raft + both shore huts
        // Raft mid-channel.
        const rw = rng.int(84, 120);
        const rcx = wl.x + Math.round(rng.range(rw / 2 + 30, wl.w - rw / 2 - 30));
        const ry = wl.y - 6;
        fillRect(terrain, rcx - rw / 2, ry, rcx + rw / 2, ry + 15);
        // Stilt hut overhanging each shore edge.
        for (const hx of [wl.x - 8, wl.x + wl.w + 8]) {
          const hw = rng.int(76, 92);
          const hy = wl.y - 10;
          fillRect(terrain, hx - hw / 2, hy, hx + hw / 2, hy + 11);
          for (const sx of [Math.round(hx - hw / 2 + 6), Math.round(hx + hw / 2 - 10)]) {
            let y = hy + 12;
            while (y < WORLD_H - 2 && !isSolid(terrain, sx, y)) {
              terrain[y * WORLD_W + sx] = 1;
              y++;
            }
          }
        }
      }
      break;
    }

    case 'candy':
    case 'prism':
    case 'orchid':
    case 'mono': {
      // Rounded monoliths scattered across the field.
      stampPillars(rng.int(5, 8), { minH: 70, maxH: 150, minW: 46, maxW: 84 });
      break;
    }

    case 'golden': {
      // Occasional low stones — the prairie stays open for long shots.
      stampPillars(rng.int(3, 5), { minH: 60, maxH: 120, minW: 50, maxW: 90 });
      break;
    }

    case 'void': {
      // Sparse flat-topped monoliths — silhouette cover under the stars.
      stampPillars(rng.int(3, 5), { minH: 130, maxH: 230, minW: 54, maxW: 96, rect: true });
      break;
    }

    case 'magma': {
      // Basalt causeway: rectangular columns of varying height.
      stampPillars(rng.int(9, 13), { minH: 90, maxH: 210, minW: 38, maxW: 72, rect: true });
      break;
    }

    case 'sunset':
    case 'mint': {
      // Ziggurat steps accenting the terraced profile.
      for (let i = 0, n = rng.int(2, 4); i < n; i++) {
        stampSteps(
          rng.range(WORLD_W * 0.15, WORLD_W * 0.85),
          rng.int(150, 230),
          rng.int(3, 5),
          rng.int(34, 52),
        );
      }
      break;
    }

    default:
      break; // plain terrain themes
  }

  return structs;
}

/** No spawn pad within 44px — pad-flattening must not guillotine details. */
function clearOfPads(
  x: number,
  w: number,
  taken: { x: number; y: number }[],
): boolean {
  return !taken.some((s) => s.x > x - 44 && s.x < x + w + 44);
}

/**
 * Phase 2 — thin details, AFTER pads are frozen. Masts, rooftop water
 * tanks, hoodoos and ice spires stand on things pads might have shaved, so
 * they only plant where flattening can never reach.
 */
function stampStructureDetails(
  terrain: Uint8Array,
  rng: Rng,
  themeId: ThemeId,
  structs: Structs,
  taken: { x: number; y: number }[],
  profileAt: (x: number) => number,
): void {
  // --- rooftop water tanks (city towers, harbor warehouses) ----------------
  if (themeId === 'city' || themeId === 'skyline' || themeId === 'neon' || themeId === 'harbor') {
    const wide = structs.roofs.filter((r) => r.w >= 104);
    for (let i = 0; i < wide.length && i < 3; i++) {
      const r = wide[i]; // distinct roofs, left to right
      const tw = rng.int(64, 80);
      const cx = r.x + Math.round(r.w / 2) + rng.int(-16, 16);
      const tx0 = cx - tw / 2;
      if (!clearOfPads(tx0, tw, taken)) continue;
      // Solid drum — a notch from a pad reads as wear, never as a float.
      fillRect(terrain, tx0, r.roof - 44, tx0 + tw - 1, r.roof + 2);
    }
  }

  // --- ship rigging (harbor) ----------------------------------------------
  if (themeId === 'harbor') {
    for (const s of structs.ships) {
      const span = s.x1 - s.x0;
      // Cabin near one end.
      const cw = rng.int(90, 130);
      const ch = rng.int(30, 40);
      const cx0 = rng.next() < 0.5 ? s.x0 + 42 : s.x1 - 42 - cw;
      if (clearOfPads(cx0, cw, taken)) {
        fillRect(terrain, cx0, s.deckY - ch, cx0 + cw - 1, s.deckY + 6);
      }
      // Two masts with crow's nests.
      for (const f of [0.32, 0.68]) {
        const mx = Math.floor(s.x0 + span * f);
        const mh = rng.int(120, 170);
        if (!clearOfPads(mx - 6, 12, taken)) continue;
        fillRect(terrain, mx - 3, s.deckY - mh, mx + 3, s.deckY + 4);
        fillRect(terrain, mx - 16, s.deckY - mh - 10, mx + 16, s.deckY - mh - 1);
      }
      // Scattered cargo crates.
      for (let c = 0; c < 3; c++) {
        const bw = rng.int(30, 44);
        const bx = s.x0 + 50 + Math.floor(rng.next() * Math.max(1, span - 100 - bw));
        if (!clearOfPads(bx, bw, taken)) continue;
        fillRect(terrain, bx, s.deckY - rng.int(22, 30), bx + bw - 1, s.deckY + 4);
      }
    }
  }

  // --- igloo rooms (snow): hollow now that pads are frozen -----------------
  if (themeId === 'snow') {
    for (const ig of structs.igloos) {
      const { cx, g, r } = ig;
      const ir = r - 16;
      // Interior (roof ~13px at the crown; the crown pad's fill leaves that
      // slab intact because interior ceilY always sits 16*0.8 ≈ 13px below
      // the outer dome). Floor follows the LOCAL ground uphill, never dips
      // below the intended level downhill.
      for (let dx = -ir; dx <= ir; dx++) {
        const x = Math.round(cx + dx);
        if (x < 1 || x >= WORLD_W - 1) continue;
        const ceilY = g - Math.round(Math.sqrt(ir * ir - dx * dx) * 0.8);
        const floorY = Math.min(g, Math.floor(profileAt(x)));
        for (let y = ceilY; y < floorY; y++) terrain[y * WORLD_W + x] = 0;
      }
      // Door punched through one wall.
      const side = rng.next() < 0.5 ? -1 : 1;
      for (let k = ir - 4; k <= r + 2; k++) {
        const x = Math.round(cx + side * k);
        if (x < 1 || x >= WORLD_W - 1) continue;
        const floorY = Math.min(g, Math.floor(profileAt(x)));
        for (let y = g - 26; y < floorY; y++) terrain[y * WORLD_W + x] = 0;
      }
    }
  }

  // --- hoodoos (desert/canyon) & ice spires (snow): cover to hide behind --
  if (themeId === 'desert' || themeId === 'canyon' || themeId === 'snow') {
    const count = rng.int(3, 5);
    for (let i = 0; i < count; i++) {
      const cx = Math.round(rng.range(WORLD_W * 0.06, WORLD_W * 0.94));
      if (!clearOfPads(cx - 26, 52, taken)) continue;
      const g = Math.floor(profileAt(cx));
      const top = Math.max(250, g - rng.int(70, 150));
      if (themeId === 'snow') {
        // Ice spire: a tapering cone, too thin to spawn on.
        for (let y = top; y < g; y++) {
          const t = (y - top) / Math.max(1, g - top);
          const half = Math.max(1, Math.round(2 + t * 10));
          fillRect(terrain, cx - half, y, cx + half, y);
        }
        seat(terrain, cx - 12, cx + 12, g, profileAt, 12);
      } else {
        // Hoodoo: slender stem wearing a wider, harder capstone.
        const halfStem = rng.int(8, 14);
        fillRect(terrain, cx - halfStem, top + 10, cx + halfStem, g - 1);
        const capW = rng.int(20, 26);
        fillRect(terrain, cx - capW, top, cx + capW, top + 12);
        seat(terrain, cx - halfStem, cx + halfStem, g, profileAt, 12);
      }
    }
  }
}

